
class InstagramGallery extends HTMLElement {
  constructor() {
    super();
    this.isAutoplayInitialized = false;
    this.isGlightboxInitialized = false;
    this.glightbox = null;
    this.onSectionLoad = this.onSectionLoad.bind(this);
  }

  async connectedCallback() {
    await this.renderFeedItems();
    this.handleAutoplay();
    this.initGlightboxWhenReady();

    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:section:load", this.onSectionLoad);
    }
  }

  async renderFeedItems() {
    const feedUrl = this.getAttribute("data-feed-url");
    if (!feedUrl) return;

    try {
      const response = await fetch(feedUrl);
      if (!response.ok) return;

      const data = await response.json();
      const items = this.getFeedItems(data);
      if (!items.length) return;

      const sectionId = this.getAttribute("data-section-id");
      const roundedCorners = this.getAttribute("data-img-rounded-corners") === "true";
      const lightboxHeading = this.getAttribute("data-lightbox-heading") || "Instagram";
      const lightboxLogo = this.getItemValue(data, ["profilePictureUrl", "profile_picture_url"]) || "";
      const lightboxLogoAlt = this.getItemValue(data, ["username"]) || lightboxHeading;
      const goToPostLabel = this.getAttribute("data-go-to-post-label") || "Go to post";

      this.innerHTML = items.map((item, index) => {
        const mediaType = this.getItemValue(item, ["mediaType", "media_type"]);
        const mediaUrl = this.getItemValue(item, ["mediaUrl", "media_url"]);
        const previewImage =
          this.getItemValue(item, ["videoThumbnail", "video_thumbnail"]) ||
          item?.sizes?.medium?.mediaUrl ||
          item?.sizes?.small?.mediaUrl ||
          mediaUrl;
        const image = mediaType === "VIDEO" ? previewImage : mediaUrl || previewImage;
        const mediaHref = mediaType === "VIDEO" ? mediaUrl || previewImage : image;
        const postUrl = this.getItemValue(item, ["permalink", "url", "link"]) || "#";
        const caption = this.getItemValue(item, ["caption", "captions", "title"]) || "Instagram post";
        const formattedCaption = this.formatCaption(caption);
        const lightboxDescriptionId = `instagram-gallery-lightbox-${sectionId}-${index}`;
        if (!image) return "";

        return `
          <div class="instagram-gallery-item scroll-animate-fade-in-up" style="animation-delay: ${index * 0.1}s" role="listitem">
            <div class="instagram-gallery-item-inner" data-img-rounded-corners="${roundedCorners}">
              <a href="${this.escapeAttribute(mediaHref)}" class="instagram-gallery-item-link" data-gallery="gallery-${sectionId}" data-description=".${lightboxDescriptionId}" target="_blank" rel="noopener noreferrer">
                <img src="${this.escapeAttribute(image)}" class="img-fluid" alt="${this.escapeAttribute(caption)}" loading="lazy">
              </a>
              <div class="${lightboxDescriptionId} glightbox-desc">
                <div class="instagram-gallery-lightbox-header">
                  ${lightboxLogo ? `<img src="${this.escapeAttribute(lightboxLogo)}" width="40" height="40" alt="${this.escapeAttribute(lightboxLogoAlt)}">` : ""}
                  <h3 class="heading">${this.escapeAttribute(lightboxHeading)}</h3>
                </div>
                <div class="instagram-gallery-lightbox-description description rte">${formattedCaption}</div>
                <div class="instagram-gallery-lightbox-footer">
                  <a href="${this.escapeAttribute(postUrl)}" class="btn btn-sm btn-outline btn-with-icon btn-full" target="_blank" rel="noopener noreferrer">
                    <svg xmlns="http://www.w3.org/2000/svg" class="me-4" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16.98 0a6.9 6.9 0 0 1 5.08 1.98A6.94 6.94 0 0 1 24 7.02v9.96c0 2.08-.68 3.87-1.98 5.13A7.14 7.14 0 0 1 16.94 24H7.06a7.06 7.06 0 0 1-5.03-1.89A6.96 6.96 0 0 1 0 16.94V7.02C0 2.8 2.8 0 7.02 0h9.96zm.05 2.23H7.06c-1.45 0-2.7.43-3.53 1.25a4.82 4.82 0 0 0-1.3 3.54v9.92c0 1.5.43 2.7 1.3 3.58a5 5 0 0 0 3.53 1.25h9.88a5 5 0 0 0 3.53-1.25 4.73 4.73 0 0 0 1.4-3.54V7.02a5 5 0 0 0-1.3-3.49 4.82 4.82 0 0 0-3.54-1.3zM12 5.76c3.39 0 6.2 2.8 6.2 6.2a6.2 6.2 0 0 1-12.4 0 6.2 6.2 0 0 1 6.2-6.2zm0 2.22a3.99 3.99 0 0 0-3.97 3.97A3.99 3.99 0 0 0 12 15.92a3.99 3.99 0 0 0 3.97-3.97A3.99 3.99 0 0 0 12 7.98zm6.44-3.77a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z"/>
                    </svg>
                    ${this.escapeAttribute(goToPostLabel)}
                  </a>
                </div>
              </div>
            </div>
          </div>
        `;
      }).join("");
    } catch (error) {
      return;
    }
  }

  getFeedItems(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.posts)) return data.posts;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  }

  getItemValue(item, keys = []) {
    for (const key of keys) {
      if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== "") {
        return item[key];
      }
    }

    return null;
  }

  escapeAttribute(text = "") {
    const temp = document.createElement("div");
    temp.textContent = String(text);
    return temp.innerHTML;
  }

  formatCaption(text = "") {
    return this.escapeAttribute(text).replace(/\r?\n/g, "<br>");
  }

  disconnectedCallback() {
    this.glightbox?.destroy();
    this.glightbox = null;
    document.removeEventListener("shopify:section:load", this.onSectionLoad);
  }

  handleAutoplay() {
    if (this.isAutoplayInitialized) return;
    this.isAutoplayInitialized = true;

    const autoplay = this.getAttribute('data-autoplay') === 'true';
    if (!autoplay) return;

    let intervalId;
    const autoplayTime = Number(this.getAttribute('data-autoplay-time') || 3000);

    const startAutoplay = () => {
      intervalId = setInterval(() => {
        const items = this.querySelectorAll('.instagram-gallery-item');
        const itemWidth = items[0]?.offsetWidth || 0;
        const maxScrollLeft = this.scrollWidth - this.clientWidth;

        if (this.scrollLeft + itemWidth > maxScrollLeft) {
          this.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scrollBy({ left: itemWidth, behavior: 'smooth' });
        }
      }, autoplayTime);
    };

    const stopAutoplay = () => {
      clearInterval(intervalId);
    };

    this.addEventListener('mouseenter', stopAutoplay);
    this.addEventListener('mouseleave', startAutoplay);

    startAutoplay();
  }

  getSectionId() {
    const galleryTrigger = this.querySelector('[data-gallery^="gallery-"]');
    if (!galleryTrigger) return null;

    return galleryTrigger.dataset.gallery.replace("gallery-", "");
  }

  initGlightboxWhenReady() {
    if (!this.getSectionId()) return;

    if (window.GLightbox) {
      this.initGlightbox();
      return;
    }

    const script = document.querySelector('script[src*="vendor-glightbox"]');
    script?.addEventListener("load", () => this.initGlightbox(), { once: true });
  }

  initGlightbox() {
    if (this.isGlightboxInitialized || !window.GLightbox) return;

    const sectionId = this.getSectionId();
    if (!sectionId) return;

    this.isGlightboxInitialized = true;

    this.glightbox = window.GLightbox({
      selector: `[data-gallery="gallery-${sectionId}"]`,
      loop: true,
      openEffect: "zoom",
      closeEffect: "fade",
      descPosition: "right",
    });

    this.glightbox.on("open", () => {
      const lightboxBody = document.getElementById("glightbox-body");
      if (!lightboxBody) return;

      lightboxBody.setAttribute("data-source", "instagram-gallery");
      lightboxBody.classList.add("is-instagram-gallery");
    });

    this.glightbox.on("close", () => {
      const lightboxBody = document.getElementById("glightbox-body");
      if (!lightboxBody) return;

      lightboxBody.removeAttribute("data-source");
      lightboxBody.classList.remove("is-instagram-gallery");
    });
  }

  onSectionLoad(event) {
    if (event.detail?.sectionId !== this.getAttribute("data-section-id")) return;

    this.isGlightboxInitialized = false;
    this.glightbox?.destroy();
    this.glightbox = null;
    this.renderFeedItems().then(() => this.initGlightboxWhenReady());
  }
}
customElements.define('instagram-gallery', InstagramGallery);
